import { HelmetProvider } from 'react-helmet-async';
import Navbar from "./components/navbar/Navbar";
import MainContainer from "./components/MainContainer";
import HomePage from "./pages/HomePage";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AddEditActivity from "./pages/AddEditActivity";
import HandleActivities from "./pages/HandleActivities";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import RegisterLoginPage from "./pages/RegisterLoginPage";
import { getLoading, getLogged, getUnlogged } from "./redux/userSlice";
import ProfilePage from "./pages/ProfilePage";
import ResetPasswordRequest from "./pages/ResetPasswordRequest";
import SetNewPassword from "./pages/SetNewPassword";
import ActivityDetails from "./pages/ActivityDetails";
import ProtectedRoute from "./components/ProtectedRoute";
import AllActivities from "./pages/AllActivities";
import Footer from "./components/Footer";
import HandleShop from "./pages/HandleShop";
import HandleAdminBroadcast from "./pages/HandleAdminBroadcast";
import HandleHomeCarousel from "./pages/HandleHomeCarousel";
import AddEditProduct from "./pages/AddEditProduct";
import ProductDetails from "./pages/ProductDetails";
import MapPage from "./pages/MapPage";
import Test from "./components/test";
import ChooseCategory from "./pages/ChooseCategory";
import PhotosGallery from "./pages/PhotosGallery";
import AddEditCompany from "./pages/AddEditCompany";
import HandleCompanies from "./pages/HandleCompanies";
import CompanyDetails from "./pages/CompanyDetails";
import BookingWizardLayout from "./pages/booking/wizard/BookingWizardLayout";
import StepOption from "./pages/booking/wizard/StepOption";
import StepSchedule from "./pages/booking/wizard/StepSchedule";
import StepParticipants from "./pages/booking/wizard/StepParticipants";
import StepSummary from "./pages/booking/wizard/StepSummary";
import StepSuccess from "./pages/booking/wizard/StepSuccess";
import CompanyHandleRequest from "./pages/booking/CompanyHandleRequest";
import UserPurchaseResult from "./pages/booking/UserPurchaseResult";
import './i18n';
import HandleActivityTranslation from "./pages/HandleActivityTranslation.jsx";
import WhoWeAre from "./pages/WhoWeAre.jsx";
import Contacts from "./pages/Contacts.jsx";
import BaseBar from "./components/navigationBar/BaseBar.jsx";
import PartnerPage from "./pages/PartnerPage.jsx";
import CookieBanner from "./components/cookies/CookieBanner.jsx";
import PullToRefresh from "./components/pullToRefresh/PullToRefresh.jsx";
import AppUpdateGate from "./components/AppUpdateGate.jsx";
import { initConsentOnBoot } from "./utils/cookieConsent";
import PrivacyPolicy from "./pages/PrivacyPolicy.jsx";
import Impressum from "./pages/Impressum.jsx";
import UserPurchaseDataCheck from "./components/booking/UserPurchaseDataCheck.js";
import SetUserData from "./pages/SetUserData.jsx";
import UserTermsAndConditions from "./pages/UserTermsAndConditions.jsx";
import ProductPurchased from "./pages/shopOrders/ProductPurchased.jsx";
import SetShippingData from "./pages/SetShippingData.jsx";
import UserShippingDataCheck from "./components/shopOrders/UserShippingDataCheck.js";
import ConfirmOrderRequest from "./pages/shopOrders/ConfirmOrderRequest.jsx";
import ScrollToTop from "./components/ScrollToTop.js";
import DeepLinkHandler from "./components/DeepLinkHandler.js";
import Cart from "./pages/Cart.jsx";
import { updateCartFromLocalStorage } from "./redux/cartSlice.js";
import CheckoutPage from "./pages/shopOrders/CheckoutPage.jsx";
import PartnerUserTermsAndConditions from "./pages/PartnerTermsAndConditions.jsx";
import ImageDisclaimer from "./pages/ImageDisclaimer.jsx";
import DownloadTutorial from "./pages/DownloadTutorial.jsx";
import AppLanding from "./pages/AppLanding.jsx";
import StripeReturn from "./pages/StripeReturn.jsx";
import DeleteAccountConfirm from "./pages/DeleteAccountConfirm.jsx";
import DeleteAccountRequest from "./pages/DeleteAccountRequest.jsx";
import { FavouritesPage } from "./pages/FavouritesPage";
import Stats from "./pages/Stats";
import LayoutAdminDashboard from "./layouts/LayoutAdminDashboard";
import { isLocalDebugBuild } from "./utils/buildInfo";
import StatusBarBackdrop from "./components/StatusBarBackdrop";

// Footer nascosto su rotte fullscreen (es. mappa)
const FOOTER_HIDDEN_PATHS = ['/map-page', '/register-login'];
const ConditionalFooter = () => {
  const { pathname } = useLocation();
  if (FOOTER_HIDDEN_PATHS.includes(pathname)) return null;
  return <Footer />;
};

// Navbar nascosta sulla login: è un overlay fullscreen con selettore lingua
// proprio. Senza questo, il LanguageSelector della Navbar (z-30) "bucava"
// sopra l'overlay (z-5) finendo in una posizione casuale.
const NAVBAR_HIDDEN_PATHS = ['/register-login'];
const ConditionalNavbar = () => {
  const { pathname } = useLocation();
  if (NAVBAR_HIDDEN_PATHS.includes(pathname)) return null;
  return <Navbar />;
};

function App() {

  // Refresh token, se valido e non scaduto
  const dispatch = useDispatch();
  
  const sendRefresh = async () => {
    try {
      dispatch(getLoading(true))
      const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/refresh-token`;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('ticino-wild-token')}`
      };
      const options = { method: 'POST', headers }
      const response = await fetch(url, options);
      if (response.ok) {
        const result = await response.json();
        localStorage.setItem('ticino-wild-token', result.payload.token);
        dispatch(getLogged(result.payload));
      } else {
        dispatch(getUnlogged());
      }
    } catch (error) {
      dispatch(getUnlogged());
    }
  }

  // Check login session
  const token = localStorage.getItem('ticino-wild-token');
  useEffect(() => {
    if (token) {
      sendRefresh();
    } else {
      dispatch(getUnlogged());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Gestisco la disattivazione dello scroll (SearchBar aperta)
  const { isActive } = useSelector(state => state.scroll);
  useEffect(() => {
    if (!isActive) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    // Cleanup
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isActive]);

  // CARRELLO
  // Verifico che sia presente cart in localStorage
  useEffect(() => {
    const storedCart = localStorage.getItem('ticino-wild-cart');
    if (storedCart) dispatch(updateCartFromLocalStorage(JSON.parse(storedCart)));
  }, [dispatch]);

  // CONSENSO COOKIE: applica a gtag la scelta già salvata (se l'utente ha già
  // scelto in una sessione precedente). Se non c'è scelta, Consent Mode resta
  // in default-deny (vedi public/index.html) e CookieBanner si mostra.
  useEffect(() => {
    initConsentOnBoot();
  }, []);

  // BADGE BUILD LOCALE: visibile solo su build nativa Debug (lanciata da
  // Android Studio / Xcode sul device), NON sulle app scaricate da
  // TestFlight / Google Play. Serve a riconoscere a colpo d'occhio che sullo
  // smartphone si sta guardando la build appena compilata in locale.
  const [showDevBadge, setShowDevBadge] = useState(false);
  useEffect(() => {
    isLocalDebugBuild().then(setShowDevBadge);
  }, []);
  const serverLabel = (process.env.REACT_APP_SERVER_BASE_URL || '').includes('staging') ? 'staging' : 'prod';

  return (
    <HelmetProvider>
      <Router>
        <MainContainer>
          {/* Pull-to-refresh: gesto swipe-down attivo solo su app nativa (in web è inerte).
              Le pagine registrano il refetch via useRefreshHandler; le altre ricadono sul reload. */}
          <PullToRefresh>
          <StatusBarBackdrop />
          {/* Banner/blocco "Aggiorna l'app": solo nativo, guidato dal server (GET /config/app-version) */}
          <AppUpdateGate />
          <ConditionalNavbar />
          <ScrollToTop />
          <DeepLinkHandler />
          <Routes>
          {/* Aggiungere uno scrollToTop automatico per ogni cambio di rotta */}
          <Route exact path="/" element={<HomePage />} />
          <Route exact path="/register-login" element={<RegisterLoginPage />} />
          <Route exact path="/reset-password" element={<ResetPasswordRequest />} />
          <Route exact path="/set-new-password" element={<SetNewPassword />} />
          <Route exact path="/choose-category" element={<ChooseCategory />} />
          {/* <Route exact path="/shop" element={<Shop />} /> */}
          <Route exact path="/test" element={<Test />} />
          <Route exact path="/company-handle-request/:requestId" element={<CompanyHandleRequest />} />
          <Route exact path="/who-we-are" element={<WhoWeAre />} />
          <Route exact path="/contacts" element={<Contacts />} />
          <Route exact path="/partner" element={<PartnerPage />} />
          <Route exact path="/terms-and-conditions" element={<UserTermsAndConditions />} />
          <Route exact path="/partner-terms-and-conditions" element={<PartnerUserTermsAndConditions />} />
          <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route exact path="/impressum" element={<Impressum />} />
          <Route exact path="/image-disclaimer" element={<ImageDisclaimer />} />
          <Route exact path="/download-app-tutorial" element={<DownloadTutorial />} />
          {/* Landing download app: raggiunta solo da link esterni (QR/social), nessun link interno. Solo web. */}
          <Route exact path="/app" element={<AppLanding />} />
          <Route exact path="/stripe-return" element={<StripeReturn />} />
          <Route exact path="/account/delete-confirm" element={<DeleteAccountConfirm />} />
          <Route exact path="/account/delete-request" element={<DeleteAccountRequest />} />
          <Route exact path="/all-activities" element={<AllActivities />} />
          <Route exact path="/map-page" element={<MapPage />} />

          <Route element={<ProtectedRoute requiredLevel={1} />}>
            <Route exact path="/my-profile" element={<ProfilePage />} />
            <Route exact path="/favourites" element={<FavouritesPage />} />
            <Route exact path="/activity-details" element={<ActivityDetails />} />
            <Route exact path="/activity-gallery" element={<PhotosGallery />} />
            <Route exact path="/product-details" element={<ProductDetails />} />
            <Route exact path="/request/success" element={<UserPurchaseResult />} /> {/* Acquisto attività avvenuto (?) */}
            <Route exact path="/product-purchase-success" element={<ProductPurchased />} /> {/* Acquisto in shop avvenuto */}
            <Route exact path="/cart" element={<Cart />} />
            <Route exact path="/checkout" element={<CheckoutPage />} />

            {/* Middleware di check dati per acquisto esperienza */}
            {/* Wizard di prenotazione multi-step: uno step per sotto-route, così il back di
                sistema (Android incluso) naviga tra gli step senza perdere i dati inseriti */}
            <Route element={<UserPurchaseDataCheck />}>
              <Route path="/send-request/:activityId" element={<BookingWizardLayout />}>
                <Route index element={<Navigate to="opzione" replace />} />
                <Route path="opzione" element={<StepOption />} />
                <Route path="data" element={<StepSchedule />} />
                <Route path="partecipanti" element={<StepParticipants />} />
                <Route path="riepilogo" element={<StepSummary />} />
                <Route path="inviata" element={<StepSuccess />} />
              </Route>
            </Route>
            <Route exact path="/set-user-data" element={<SetUserData />} />

            {/* Middleware di check dati per acquisto prodotto in shop */}
            <Route element={<UserShippingDataCheck />}>
              <Route exact path="/confirm-order-request" element={<ConfirmOrderRequest />} />
            </Route>
            <Route exact path="/set-shipping-data" element={<SetShippingData />} />
            {/* <Route exact path="/payment-success" /> */}
          </Route>

          <Route element={<ProtectedRoute requiredLevel={2} />}>
            <Route element={<LayoutAdminDashboard />}>
              <Route exact path="/stats" element={<Stats />} />
              <Route exact path="/handle-activities" element={<HandleActivities />} />
              <Route exact path="/handle-companies" element={<HandleCompanies />} />
              <Route exact path="/handle-shop" element={<HandleShop />} />
              <Route exact path="/handle-broadcast" element={<HandleAdminBroadcast />} />
              <Route exact path="/handle-home-carousel" element={<HandleHomeCarousel />} />
            </Route>
            <Route exact path="/add-edit-activity" element={<AddEditActivity />} />
            <Route exact path="/add-edit-company" element={<AddEditCompany />} />
            <Route exact path="/add-edit-product" element={<AddEditProduct />} />
            <Route exact path="/company-details" element={<CompanyDetails />} />
            <Route exact path="/handle-activity-translation" element={<HandleActivityTranslation />} />
          </Route>
        </Routes>
        <ConditionalFooter />
        <BaseBar />
        <CookieBanner />
        {showDevBadge && (
          <div
            style={{
              position: 'fixed',
              // Fluttuante sopra la BaseBar (fixed bottom-0, py-4 → ~80px),
              // 12px di stacco sopra il bordo superiore della barra.
              bottom: 'calc(80px + 12px)',
              right: 8,
              zIndex: 9999,
              background: '#b05704',
              color: 'white',
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 4,
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
            }}
          >
            {`LOCALE · ${serverLabel}`}
          </div>
        )}
          </PullToRefresh>
      </MainContainer>
    </Router>
    </HelmetProvider>
  );
}

export default App;
